import { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient, useMutation } from "@apollo/client";
import moment from "moment";
import { useTranslation } from "components/use-hooks";
import { errorNotification, successNotification } from "components/request-result";
import {
    STAFF_MANUAL_CHECKOUT_SESSION_FRAGMENT,
    WORK_SESSION_MANUAL_CHECKOUT,
} from "graphql/mutation/staff-management-gql";
import { canManuallyCheckoutStaffSession } from "../helpers/staff-attendance-session-helper";


const findSession = (groups, id) => groups
    .flatMap((group) => group.workers)
    .flatMap((row) => row.sessions)
    .find((session) => String(session.id) === String(id));

export const isConfirmedStaffManualCheckout = (session, requestedID) => (
    Boolean(session) &&
    String(session.id) === String(requestedID) &&
    Boolean(session.check_out_at) &&
    moment(session.check_out_at, [ "YYYY-MM-DD HH:mm:ss", moment.ISO_8601 ], true).isValid() &&
    session.getCheckOutStatus === "COMPLETED"
);

const useStaffManualCheckout = ({ contextKey, groups, refresh, loading }) => {
    const client = useApolloClient();
    const translations = useTranslation();
    // Only verified, current-context results may update the normalized cache.
    const [ runCheckout ] = useMutation(WORK_SESSION_MANUAL_CHECKOUT, { fetchPolicy: "no-cache" });
    const [ target, setTarget ] = useState(null);
    const [ pendingSessionID, setPendingSessionID ] = useState(null);
    const pending = useRef(false);
    const mounted = useRef(true);
    const context = useRef({ key: contextKey });
    if (context.current.key !== contextKey) context.current = { key: contextKey };
    const latest = useRef();
    latest.current = { context: context.current, groups, refresh, loading, target, translations };

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    useEffect(() => { setTarget(null); }, [ contextKey ]);

    const request = useCallback((attendanceRow, session) => {
        const current = latest.current;
        if (pending.current || current.loading || !canManuallyCheckoutStaffSession(
            findSession(current.groups, session?.id)
        ))
        {
            return;
        }
        setTarget({ attendanceRow, session, context: current.context });
    }, []);

    const cancel = useCallback(() => {
        if (!pending.current) setTarget(null);
    }, []);

    const confirm = useCallback(async () => {
        const current = latest.current;
        const selected = current.target;
        if (pending.current || !selected || selected.context !== current.context) return;

        const text = (key) => current.translations[ `STAFF_MANAGEMENT.${ key }` ] || `STAFF_MANAGEMENT.${ key }`;
        const notifyError = (key, error) => errorNotification({
            label: text(key),
            reason: error?.message || text(key),
        });

        const session = findSession(current.groups, selected.session.id);
        if (current.loading || !canManuallyCheckoutStaffSession(session))
        {
            setTarget(null);
            notifyError("ManualCheckout_StaleSession");
            return;
        }

        pending.current = true;
        setPendingSessionID(String(session.id));
        const isCurrent = () => mounted.current && latest.current.context === current.context;
        const refreshCurrent = async (confirmed = false) => {
            if (!isCurrent()) return;
            try
            {
                const result = await current.refresh();
                if (result?.error) throw result.error;
                if (result?.errors?.length) throw new Error(result.errors.map((error) => error.message).join("; "));
            }
            catch (error)
            {
                if (isCurrent() && confirmed) notifyError("ManualCheckout_RefreshError", error);
            }
        };

        try
        {
            let payload;
            try
            {
                const result = await runCheckout({ variables: { id: String(session.id) } });
                payload = result.data?.workSessionManualCheckout;
                if (!isCurrent()) return;
                if (!isConfirmedStaffManualCheckout(payload?.workSession, session.id))
                {
                    throw new Error(payload?.message);
                }
            }
            catch (error)
            {
                if (!isCurrent()) return;
                setTarget(null);
                notifyError("ManualCheckout_Error", error);
                // Also reconcile concurrent checkout or an uncertain network outcome.
                await refreshCurrent();
                return;
            }

            setTarget(null);
            try
            {
                client.writeFragment({
                    id: client.cache.identify(payload.workSession),
                    fragment: STAFF_MANUAL_CHECKOUT_SESSION_FRAGMENT,
                    fragmentName: "StaffManualCheckoutSession",
                    data: payload.workSession,
                });
            }
            catch (error)
            {
                notifyError("ManualCheckout_RefreshError", error);
            }
            successNotification({
                title: payload.label || text("ManualCheckout_Success"),
                description: payload.message,
            });
            await refreshCurrent(true);
        }
        finally
        {
            pending.current = false;
            if (mounted.current) setPendingSessionID(null);
        }
    }, [ client, runCheckout ]);

    return {
        target: target?.context === context.current ? target : null,
        pendingSessionID,
        request,
        confirm,
        cancel,
    };
};

export default useStaffManualCheckout;
